import React, {
  useState,
  useEffect,
  useRef,
  CSSProperties,
  useMemo,
  ReactNode,
} from "react";
import { InputField, InputFieldProps } from "../InputField/InputField";
import { Icon } from "../Icon/icon";
import stylesheet from "./Dropdown.module.scss";
import { Chip } from "../Chip";
import { Checkbox } from "../Checkbox";
import { LoadingSpinner } from "../LoadingSpinner";
import { LoadingDots } from "../LoadingDots";
import { Accordion } from "../Accordion/Accordion";
import { DropdownMenuItem } from "./DropdownMenuItem";

type AppendProps = { setIsOpen: React.Dispatch<React.SetStateAction<boolean>> };

export interface Option<T = string> {
  icon?: string;
  label: string;
  value: T;
  group?: string;
}

type BaseDropdownProps = Omit<
  InputFieldProps,
  "value" | "type" | "onChange" | "required"
> & {
  defaultInputValue?: string;
  options?: Option[];
  isLoadingOptions?: boolean;
  prependMenu?: ReactNode | ((props: AppendProps) => ReactNode);
  appendMenu?: ReactNode | ((props: AppendProps) => ReactNode);
  menuPlacement?: "top" | "bottom";
  maxMenuHeight?: number;
  clearable?: boolean;
  required?: boolean;
  lockedValues?: string[];
  fetchOptions?: (input: string) => Promise<Option[] | undefined>;
  onFetchFailed?: (error: unknown) => void;
  onInputChange?: (input: string) => void;
  /** Called when an option is highlighted by mouse or keyboard, e.g. to
   * prefetch what selecting it will need. */
  onHighlight?: (option: Option) => void;
  startSearchText?: string;
  noResultsText?: string;
  searchingText?: string;
  selectChip?: {
    style?: CSSProperties;
    replaceLabelsWithIcons?: boolean;
  };
  className?: string;
  expandIcon?: string;
  styles?: {
    container?: CSSProperties;
    input?: CSSProperties;
    menu?: CSSProperties;
  };
};

type SingleSelectProps = BaseDropdownProps & {
  isMulti?: false;
  value?: Option | Option["value"];
  showSelectAllOption?: false;
  selectAllLabel?: never;
} & (
    | { clearable?: false; onChange: (option: Option) => void }
    | { clearable: boolean; onChange: (option: Option | undefined) => void }
  );

type MultiSelectProps = BaseDropdownProps & {
  isMulti: true;
  value?: (Option | Option["value"])[];
  showSelectAllOption?: boolean;
  selectAllLabel?: string;
} & (
    | { clearable?: false; onChange: (option: Option[]) => void }
    | { clearable: boolean; onChange: (option: Option[] | undefined) => void }
  );

export type DropdownProps = SingleSelectProps | MultiSelectProps;

export function Dropdown({
  defaultInputValue,
  value: rawValue,
  prependMenu,
  appendMenu,
  options,
  isLoadingOptions,
  isMulti,
  showSelectAllOption = false,
  selectAllLabel = "Select All",
  placeholder = "Select...",
  menuPlacement = "bottom",
  maxMenuHeight = 400,
  clearable = false,
  fetchOptions,
  onFetchFailed,
  onInputChange,
  onHighlight,
  onChange,
  onBlur,
  startSearchText = "Start typing to search",
  noResultsText = "No results",
  searchingText = "Searching...",
  selectChip,
  className,
  expandIcon = "expand_more",
  styles,
  required,
  lockedValues = [],
  ...inputProps
}: DropdownProps) {
  const hasValue = isMulti
    ? !!(rawValue as (Option | Option["value"])[] | undefined)?.length
    : !!(rawValue as Option | Option["value"] | undefined);
  const SELECT_ALL_VALUE = "__ALL__";
  const [inputValue, setInputValue] = useState(defaultInputValue || "");
  const [dropdownOptions, setDropdownOptions] = useState<Option[]>(
    options || [],
  );

  // Resolve raw values (e.g. a plain string) to their matching Option by
  // looking them up against the known options, falling back to a synthetic
  // Option (label === value) if no match is found yet (e.g. async options
  // still loading).
  const optionPool = options ?? dropdownOptions;
  const value = useMemo((): Option | Option[] | undefined => {
    if (rawValue == null) return undefined;

    const resolveOption = (raw: Option | Option["value"]): Option =>
      typeof raw === "object" && raw !== null
        ? raw
        : (optionPool.find((o) => o.value === raw) ??
          ({ label: String(raw), value: raw } as Option));

    return isMulti
      ? (rawValue as (Option | Option["value"])[]).map(resolveOption)
      : resolveOption(rawValue as Option | Option["value"]);
  }, [rawValue, isMulti, optionPool]);
  // `isMulti` and `value` are independent variables, so narrowing one does not
  // narrow the other's `Option | Option[]` union — split into two safely-typed
  // views instead of casting at every call site.
  const multiValue = isMulti ? (value as Option[] | undefined) : undefined;
  const singleValue = !isMulti ? (value as Option | undefined) : undefined;
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({});
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(
    menuPlacement === "bottom" ? -1 : dropdownOptions.length,
  );
  const [isSearching, setIsSearching] = useState(isLoadingOptions || false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const mouseDownRef = useRef(false);
  const optionSelectedRef = useRef(false);
  const keyboardNavigationRef = useRef(false);
  const { dark, inputBgColor, disabled, append } = inputProps;

  // Build options with optional "Select All"
  const computedOptions = useMemo(() => {
    if (!isMulti || !showSelectAllOption || !options || options.length === 1)
      return options || [];
    return [{ label: selectAllLabel, value: SELECT_ALL_VALUE }, ...options];
  }, [options, isMulti, showSelectAllOption, selectAllLabel]);

  // Track if user is actively filtering
  const isFiltering = inputValue.length > 0;

  // Separate grouped and ungrouped options while preserving order
  // Use dropdownOptions (filtered) to support search
  const { groupedOptions, ungroupedOptions, selectAllOption } = useMemo(() => {
    const groupedMap: Record<string, Option[]> = {};
    const groupOrder: string[] = [];
    const ungrouped: Option[] = [];
    let selectAll: Option | null = null;

    dropdownOptions.forEach((option) => {
      // Separate SELECT_ALL from other ungrouped options
      if (option.value === SELECT_ALL_VALUE) {
        selectAll = option;
      } else if (option.group) {
        if (!groupedMap[option.group]) {
          groupedMap[option.group] = [];
          groupOrder.push(option.group); // Track order of first appearance
        }
        groupedMap[option.group].push(option);
      } else {
        ungrouped.push(option);
      }
    });

    // Convert to array of [groupName, options] tuples in original order
    // Filter out empty groups
    const grouped = groupOrder
      .map(
        (groupName) => [groupName, groupedMap[groupName]] as [string, Option[]],
      )
      .filter(([, opts]) => opts.length > 0);

    return {
      groupedOptions: grouped,
      ungroupedOptions: ungrouped,
      selectAllOption: selectAll as Option | null,
    };
  }, [dropdownOptions, SELECT_ALL_VALUE]);

  // Create a flat list of all navigable options for keyboard navigation
  // This includes selectAll, grouped options, and ungrouped options in display order
  const navigableOptions = useMemo(() => {
    const options: Option[] = [];

    // Add Select All first if present
    if (selectAllOption) {
      options.push(selectAllOption);
    }

    // Add grouped options (all options from all groups)
    groupedOptions.forEach(([, groupOptions]) => {
      options.push(...groupOptions);
    });

    // Add ungrouped options
    options.push(...ungroupedOptions);

    return options;
  }, [selectAllOption, groupedOptions, ungroupedOptions]);

  // Called from the handlers that move the highlight, not an effect on the
  // index: that would also fire on reopening or on new options.
  const notifyHighlight = (index: number) => {
    const option = navigableOptions[index];
    if (option) onHighlight?.(option);
  };
  const highlightOnHover = (index: number) => {
    setHighlightedIndex(index);
    if (index !== highlightedIndex) notifyHighlight(index);
  };

  // Helper to get the navigable index for an option
  const getNavigableIndex = (option: Option): number => {
    return navigableOptions.findIndex((opt) => opt.value === option.value);
  };

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  // Handle group selection for multi-select
  const handleGroupSelect = (groupOptions: Option[]) => {
    if (!isMulti) return;

    const allSelected = groupOptions.every((opt) =>
      multiValue?.find((v) => v.value === opt.value),
    );

    let newSelected: Option[] = multiValue ? [...multiValue] : [];

    if (allSelected) {
      // Deselect all options in this group
      newSelected = newSelected.filter(
        (v) => !groupOptions.find((opt) => opt.value === v.value),
      );
    } else {
      // Select all options in this group
      groupOptions.forEach((opt) => {
        if (!newSelected.find((v) => v.value === opt.value)) {
          newSelected.push(opt);
        }
      });
    }

    // Handle Select All logic
    if (showSelectAllOption && options && options.length > 0) {
      // Remove __ALL__ marker first to calculate purely based on content options
      const contentSelected = newSelected.filter(
        (o) => o.value !== SELECT_ALL_VALUE,
      );
      const allOptionsSelected = options.every((o) =>
        contentSelected.some((s) => s.value === o.value),
      );

      if (allOptionsSelected) {
        newSelected = [
          { label: selectAllLabel, value: SELECT_ALL_VALUE },
          ...options,
        ];
      } else {
        newSelected = contentSelected;
      }
    }

    onChange(newSelected);
  };

  useEffect(() => {
    // Filter on static options
    if (options) {
      if (inputValue) {
        const filteredOptions = computedOptions.filter((o) =>
          o.label.toLowerCase().includes(inputValue.toLowerCase()),
        );
        setDropdownOptions(filteredOptions);
      } else {
        setDropdownOptions(computedOptions);
      }
    }
    // Async search to fetch options from backend
    else {
      if (!fetchOptions) return;
      if (!inputValue) {
        if (!optionSelectedRef.current) {
          setDropdownOptions([]);
          optionSelectedRef.current = false;
        }
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      const delayDebounceFn = setTimeout(async () => {
        try {
          const results = await fetchOptions(inputValue);
          setDropdownOptions(results || []);
        } catch (error) {
          console.error(error);
          if (onFetchFailed) onFetchFailed(error);
        } finally {
          setIsSearching(false);
        }
      }, 300);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [inputValue, options, onFetchFailed, computedOptions]);

  useEffect(() => {
    setIsSearching(isLoadingOptions || false);
  }, [isLoadingOptions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setInputValue(e.target.value);
    onInputChange?.(e.target.value);
  };

  const handleOptionClick = (option: Option) => {
    if (isMulti) {
      // Prevent deselecting locked values
      if (lockedValues.includes(option.value)) return;
      let newSelected: Option[] = multiValue ? [...multiValue] : [];
      // Remove __ALL__ marker first to calculate purely based on content options
      const contentSelected = newSelected.filter(
        (o) => o.value !== SELECT_ALL_VALUE,
      );
      const allOptionsSelected = !!(
        multiValue?.some((o) => o.value === SELECT_ALL_VALUE) ||
        options?.every((o) => contentSelected.some((s) => s.value === o.value))
      );

      // if all options are selected, proceed as if all options are in values
      if (allOptionsSelected && options) {
        newSelected = options;
      }

      const isSelected =
        allOptionsSelected || newSelected.some((o) => o.value === option.value);

      // Handle toggling on ALL option
      if (showSelectAllOption && option.value === SELECT_ALL_VALUE) {
        if (isSelected) {
          // Deselect all unlocked options, keep locked values selected
          const lockedSelected = (options || []).filter((o) =>
            lockedValues.includes(o.value),
          );
          onChange(lockedSelected);
        } else {
          // Select All
          onChange(computedOptions);
        }
        return;
      }

      // Normal option click handling
      if (isSelected) {
        newSelected = newSelected.filter(
          (o) => o.value !== option.value && o.value !== SELECT_ALL_VALUE,
        );
      } else {
        newSelected.push(option);
      }

      onChange(newSelected);
      return;
    }

    // Single select
    optionSelectedRef.current = true;
    setIsOpen(false);
    onChange(option);
    setInputValue("");
  };

  const scrollToOption = (index: number) => {
    // Only scroll if we're using keyboard navigation
    if (!keyboardNavigationRef.current) return;

    requestAnimationFrame(() => {
      if (!menuRef.current || !isOpen) return;
      // Find the highlighted element using data-nav-index attribute
      const highlightedElement = menuRef.current.querySelector(
        `[data-nav-index="${index}"]`,
      );

      if (highlightedElement) {
        // Scroll the menu container to show the highlighted element
        const menuRect = menuRef.current.getBoundingClientRect();
        const elementRect = highlightedElement.getBoundingClientRect();

        // Scroll to complete top if first element is highlighted
        if (index === 0) {
          menuRef.current.scrollTop = 0;
        }
        // Check if element is out of view
        else if (elementRect.bottom > menuRect.bottom) {
          // Scroll down
          menuRef.current.scrollTop +=
            elementRect.bottom - menuRect.bottom + 10;
        } else if (elementRect.top < menuRect.top) {
          // Scroll up
          menuRef.current.scrollTop -= menuRect.top - elementRect.top + 25;
        }
      }

      // Reset keyboard navigation flag after scrolling
      keyboardNavigationRef.current = false;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        keyboardNavigationRef.current = true;
        setHighlightedIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % navigableOptions.length;
          // Auto-expand group if navigating to a grouped option
          const { group } = navigableOptions[newIndex];
          if (group && !expandedGroups.has(group)) {
            setExpandedGroups((prev) => new Set(prev).add(group));
          }
          scrollToOption(newIndex);
          return newIndex;
        });
        notifyHighlight((highlightedIndex + 1) % navigableOptions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        keyboardNavigationRef.current = true;
        setHighlightedIndex((prevIndex) => {
          const newIndex =
            (prevIndex - 1 + navigableOptions.length) % navigableOptions.length;
          // Auto-expand group if navigating to a grouped option
          const { group } = navigableOptions[newIndex];
          if (group && !expandedGroups.has(group)) {
            setExpandedGroups((prev) => new Set(prev).add(group));
          }
          scrollToOption(newIndex);
          return newIndex;
        });
        notifyHighlight(
          (highlightedIndex - 1 + navigableOptions.length) %
            navigableOptions.length,
        );
        break;
      case "Enter":
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < navigableOptions.length
        ) {
          handleOptionClick(navigableOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (mouseDownRef.current) return;
    setIsOpen(false);
    setInputValue("");
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    setInputValue("");
    setDropdownOptions(options || []);
    if (isMulti) onChange([]);
    else (onChange as (option: Option | undefined) => void)(undefined);
  };

  const handleMouseDown = () => {
    mouseDownRef.current = true;
  };

  const handleMouseUp = () => {
    mouseDownRef.current = false;
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current && containerRef.current) {
      containerRef.current.style.zIndex = "103";
      inputRef.current.style.zIndex = "104";
      const inputRect = inputRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const placement = `${inputRect.bottom - containerRect.top - inputRect.height / 2}px`;
      const padding = `calc(${inputRect.height / 2}px + var(--wlcm-spacing-xs))`;
      setMenuStyles({
        ...(menuPlacement === "bottom"
          ? {
              top: placement,
              paddingTop: padding,
              visibility: "visible",
            }
          : {
              bottom: placement,
              paddingBottom: padding,
              visibility: "visible",
            }),
        left: `${inputRect.left - containerRect.left}px`,
        width: `${containerRect.width}px`,
      });
      if (menuPlacement === "top") {
        setTimeout(() => {
          menuRef.current?.scrollTo(0, menuRef.current.scrollHeight);
        }, 10);
      }
    } else if (!isOpen && containerRef.current && inputRef.current) {
      containerRef.current.style.zIndex = "101";
      inputRef.current.style.zIndex = "101";
    }
  }, [isOpen, value, menuPlacement]);

  // Chips rendering
  const chipsToRender = useMemo(() => {
    if (!isMulti || !multiValue) return [];
    if (
      showSelectAllOption &&
      (multiValue.some((v) => v.value === SELECT_ALL_VALUE) ||
        (options &&
          options.length > 0 &&
          options.every((opt) =>
            multiValue.some((v) => v.value === opt.value),
          )))
    ) {
      return [{ label: selectAllLabel, value: SELECT_ALL_VALUE } as Option];
    }
    return multiValue;
  }, [multiValue, isMulti, options, selectAllLabel, showSelectAllOption]);

  return (
    <div
      className={`${stylesheet.container} ${className} dropdownContainer absoluteMaxWidth`}
      style={styles?.container}
      ref={containerRef}
    >
      <InputField
        {...inputProps}
        required={required}
        nativeRequired={!!required && !hasValue}
        ref={inputRef}
        autoComplete="off"
        placeholder={isMulti && multiValue?.length ? "" : placeholder}
        prepend={
          isMulti &&
          chipsToRender.map((op) => (
            <Chip
              active
              key={op.value}
              dark={dark}
              label={op.label}
              {...(selectChip?.replaceLabelsWithIcons && op.icon
                ? { icon: op.icon }
                : {})}
              onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                handleOptionClick(op);
              }}
              aria-disabled={disabled}
              style={{
                textWrap: "nowrap",
                ...selectChip?.style,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            ></Chip>
          ))
        }
        value={isOpen || isMulti ? inputValue : singleValue?.label}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsOpen(true)}
        onBlur={handleBlur}
        onClear={clearable ? handleClear : undefined}
        displayClearBtn={
          clearable && (isMulti ? !!multiValue?.length : !!singleValue)
        }
        aria-autocomplete="list"
        aria-controls="dropdown-listbox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        append={
          isLoadingOptions ? (
            <div
              style={{
                marginRight: "var(--wlcm-spacing-xs)",
              }}
            >
              <LoadingSpinner dark={dark} />
            </div>
          ) : (
            <>
              {append || <></>}
              <Icon
                name={expandIcon}
                style={{
                  marginRight: "var(--wlcm-spacing-xs)",
                }}
              />
            </>
          )
        }
        inputBgColor={
          inputBgColor ? inputBgColor : dark ? "#000000" : "#FFFFFF"
        }
        customStyles={{
          input: styles?.input,
          scrollContainer: { paddingRight: 24 },
        }}
        style={styles?.input}
        maxLength={undefined}
      />
      {isOpen && (
        <div
          ref={menuRef}
          id="dropdown-listbox"
          role="listbox"
          aria-labelledby="dropdown-input"
          onMouseDown={handleMouseDown}
          className={`${stylesheet.dropdownMenu} ${dark && stylesheet.dark} ${stylesheet[menuPlacement]}`}
          style={{
            ...menuStyles,
            ...(maxMenuHeight ? { maxHeight: maxMenuHeight } : {}),
            ...(styles?.menu ? styles.menu : {}),
          }}
        >
          {typeof prependMenu === "function"
            ? prependMenu({ setIsOpen })
            : prependMenu}
          {isLoadingOptions ? (
            <p style={{ display: "flex", justifyContent: "center" }}>
              <LoadingDots />
            </p>
          ) : isSearching ? (
            <p>{searchingText}</p>
          ) : fetchOptions && !dropdownOptions.length && !inputValue ? (
            <p>{startSearchText}</p>
          ) : dropdownOptions.length ? (
            <ul ref={listboxRef}>
              {/* Render Select All option first if present */}
              {selectAllOption &&
                (() => {
                  const navIndex = getNavigableIndex(selectAllOption);
                  return (
                    <DropdownMenuItem
                      option={selectAllOption}
                      isMulti={isMulti}
                      isSelected={
                        !isMulti
                          ? singleValue?.value === selectAllOption.value
                          : !!(
                              multiValue?.some(
                                (op) => op.value === selectAllOption.value,
                              ) ||
                              options?.every((opt) =>
                                multiValue?.some((v) => v.value === opt.value),
                              )
                            )
                      }
                      isHighlighted={highlightedIndex === navIndex}
                      dark={dark}
                      onClick={handleOptionClick}
                      onMouseEnter={() => highlightOnHover(navIndex)}
                      navIndex={navIndex}
                    />
                  );
                })()}

              {/* Render grouped options */}
              {groupedOptions.length === 1 &&
              ungroupedOptions.length === 0 &&
              !selectAllOption ? (
                // Single group with no ungrouped and no Select All: render options directly without accordion
                groupedOptions[0][1].map((option) => {
                  const navIndex = getNavigableIndex(option);
                  return (
                    <DropdownMenuItem
                      key={option.value}
                      option={option}
                      isMulti={isMulti}
                      isSelected={
                        !isMulti
                          ? singleValue?.value === option.value
                          : !!multiValue?.find(
                              (op) =>
                                op.value === option.value ||
                                op.value === SELECT_ALL_VALUE,
                            )
                      }
                      isLocked={lockedValues.includes(option.value)}
                      isHighlighted={highlightedIndex === navIndex}
                      dark={dark}
                      onClick={handleOptionClick}
                      onMouseEnter={() => highlightOnHover(navIndex)}
                      navIndex={navIndex}
                    />
                  );
                })
              ) : (
                // Multiple groups or mixed: use accordions
                <>
                  {groupedOptions.map(([groupName, groupOptions]) => {
                    // Calculate if all options in this group are selected
                    const allGroupSelected =
                      isMulti &&
                      groupOptions.every((opt) =>
                        multiValue?.find((v) => v.value === opt.value),
                      );

                    return (
                      <li
                        key={`group-${groupName}`}
                        className={`${stylesheet.groupContainer} ${expandedGroups.has(groupName) || isFiltering ? stylesheet.groupOpen : ""}`}
                        onMouseEnter={() => setHighlightedIndex(-1)}
                      >
                        <Accordion
                          id={`dropdown-group-${groupName}`}
                          header={
                            <div
                              style={{
                                display: "flex",
                                gap: "var(--wlcm-spacing-xs)",
                              }}
                            >
                              {isMulti && (
                                <Checkbox
                                  label={groupName}
                                  hideLabel
                                  dark={dark}
                                  checked={allGroupSelected}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleGroupSelect(groupOptions);
                                  }}
                                />
                              )}
                              <span className={stylesheet.groupHeader}>
                                {groupName}
                              </span>
                            </div>
                          }
                          body={
                            <ul className={stylesheet.groupList}>
                              {groupOptions.map((option) => {
                                const navIndex = getNavigableIndex(option);
                                return (
                                  <DropdownMenuItem
                                    key={option.value}
                                    option={option}
                                    isMulti={isMulti}
                                    isSelected={
                                      !isMulti
                                        ? singleValue?.value === option.value
                                        : !!multiValue?.find(
                                            (op) =>
                                              op.value === option.value ||
                                              op.value === SELECT_ALL_VALUE,
                                          )
                                    }
                                    isLocked={lockedValues.includes(
                                      option.value,
                                    )}
                                    isHighlighted={
                                      highlightedIndex === navIndex
                                    }
                                    dark={dark}
                                    onClick={handleOptionClick}
                                    onMouseEnter={() =>
                                      highlightOnHover(navIndex)
                                    }
                                    navIndex={navIndex}
                                  />
                                );
                              })}
                            </ul>
                          }
                          expanded={
                            expandedGroups.has(groupName) || isFiltering
                          }
                          onToggle={() => toggleGroup(groupName)}
                        />
                      </li>
                    );
                  })}
                </>
              )}

              {/* Render ungrouped options at the bottom */}
              {ungroupedOptions.map((option) => {
                const navIndex = getNavigableIndex(option);
                return (
                  <DropdownMenuItem
                    key={option.value}
                    option={option}
                    isMulti={isMulti}
                    isSelected={
                      !isMulti
                        ? singleValue?.value === option.value
                        : !!multiValue?.find(
                            (op) =>
                              op.value === option.value ||
                              op.value === SELECT_ALL_VALUE,
                          )
                    }
                    isLocked={lockedValues.includes(option.value)}
                    isHighlighted={highlightedIndex === navIndex}
                    dark={dark}
                    onClick={handleOptionClick}
                    onMouseEnter={() => highlightOnHover(navIndex)}
                    navIndex={navIndex}
                  />
                );
              })}
            </ul>
          ) : (
            <p>{noResultsText}</p>
          )}
          {typeof appendMenu === "function"
            ? appendMenu({ setIsOpen })
            : appendMenu}
        </div>
      )}
    </div>
  );
}
